import { posToDOMRect } from '@tiptap/core';
import React, { useContext, useMemo, useState } from 'react';

import { EditorContext, RicosContext, ToolbarContext, useTrackRender } from './../../../ricos-context';
import { getIsExclusiveModalOpen, useModalState } from './../../../ricos-modals/modal-service-provider';
import type { IToolbarItemConfigTiptap, ToolbarButtonRenderers } from './../../../ricos-types';
import { useToolbarBoundary } from './../../toolbar-boundary';

import { FloatingToolbar } from '../../components/FloatingToolbar/FloatingToolbar';
import { MaxWidthCalculationWrapper } from '../../components/MaxWidthCalculationWrapper/MaxWidthCalculationWrapper';
import { Toolbar } from '../../components/Toolbar';
import { FLOATING_FORMATTING_TOOLBAR } from './data-hooks';

const FloatingFormattingToolbarContent = ({
  itemsConfig,
  itemsRenderers,
}: {
  itemsConfig: IToolbarItemConfigTiptap[];
  itemsRenderers: ToolbarButtonRenderers;
}) => {
  const { isMobile } = useContext(RicosContext);
  const {
    adapter: { tiptapEditor },
  } = useContext(EditorContext);
  const [maxWidth, setMaxWidth] = useState(() => tiptapEditor.view.dom.clientWidth);

  return (
    <MaxWidthCalculationWrapper setMaxWidth={setMaxWidth} data-hook={FLOATING_FORMATTING_TOOLBAR}>
      <Toolbar
        type="floating"
        isMobile={isMobile}
        toolbarItemsConfig={itemsConfig}
        toolbarItemsRenderers={itemsRenderers}
        maxWidth={maxWidth}
        alignment={'left'}
      />
    </MaxWidthCalculationWrapper>
  );
};

export const FloatingFormattingToolbar = () => {
  useTrackRender('Floating formatting toolbar');
  const { toolbars } = useContext(ToolbarContext);

  const isExclusiveModalOpen = useModalState(getIsExclusiveModalOpen);
  const {
    adapter: { tiptapEditor },
  } = useContext(EditorContext);

  const itemsConfig = useMemo(
    () => toolbars.formatting.getToolbarItemsConfig(),
    [toolbars.formatting]
  );
  const itemsRenderers = useMemo(
    () => toolbars.formatting.getToolbarItemsRenderers(),
    [toolbars.formatting]
  );

  const boundary = useToolbarBoundary();

  const showFloatingToolbar = toolbars.formatting.isVisible();

  const referenceElement = useMemo(
    () => ({
      getBoundingClientRect: () => {
        const { state, view } = tiptapEditor;
        const { from, to } = state.selection;
        return posToDOMRect(view, from, to);
      },
    }),
    [showFloatingToolbar]
  );

  if (!showFloatingToolbar || !boundary || isExclusiveModalOpen || toolbars.formatting.isEmpty()) {
    return null;
  }

  return (
    <FloatingToolbar
      hideStrategy="escaped"
      boundary={boundary}
      placement="top"
      referenceElement={referenceElement}
    >
      <FloatingFormattingToolbarContent itemsConfig={itemsConfig} itemsRenderers={itemsRenderers} />
    </FloatingToolbar>
  );
};
